import MainButton from 'components/Buttons/CommonButton';
import React from 'react';
import { DownloadedReport } from 'models/response';
import styled from 'styled-components/macro';
import { useSelector } from 'react-redux';

import selectors from 'state/selectors';
import services from 'services';
import utils from 'utils';

const WrapperReportDownloadItem = styled.div`
  display: flex;
  border: 1px solid ${({ theme }) => theme.colorPalette.grey.grey5};
  padding: 10px 11px;
  margin: 10px 0;
  border-radius: 4px;
  width: 100%;
  max-width: 1400px;
  justify-content: space-between;
  align-items: center;
`;

const LabelsWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr 1fr;
  gap: 15px;
  flex: 1;
  width: 100%;
  align-items: start;
`;

const StyledLabel = styled.div`
  font-size: ${({ theme }) => theme.fontSizePalette.xxSmall};
  color: ${({ theme }) => theme.colorPalette.grey.grey10};
  text-align: left;
  font-weight: 100;
  word-break: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
  line-height: 1.4;
`;

const StyledHeader = styled.div`
  font-size: ${({ theme }) => theme.fontSizePalette.xSmall};
  font-weight: 600;
  color: ${({ theme }) => theme.colorPalette.grey.grey10};
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  align-items: flex-start;
`;

const ButtonWrapper = styled.div`
  margin-left: 15px;
  flex-shrink: 0;
`;

interface Props {
  item: DownloadedReport;
}

export default (props: Props) => {
  const idUser = useSelector(selectors.user.getIdUser);
  const { item } = props;
  const userPreference = useSelector(selectors.user.getUserPreference);
  const t = utils.intl.useTranslator();

  const defineContentType = () => {
    switch (item.extension) {
      case 'csv':
        return 'text/csv';
      case 'xls':
        return 'application/vnd.ms-excel';
      case 'xlsx':
        return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      default:
        return 'text/csv';
    }
  };

  const buttonAction = async () => {
    if (idUser) {
      try {
        const { data } = await services.downloadExecutedReport({
          idUser,
          pathToFile: item.reportPath,
        });
        utils.file.downloadFileFromBase64Source(item.reportName, data, defineContentType(), item.extension);
      } catch (e) {
        console.error(e);
      }
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <WrapperReportDownloadItem>
      <LabelsWrapper>
        <FieldWrapper>
          <StyledHeader>{t('folder')}</StyledHeader>
          <StyledLabel>{item.folderName}</StyledLabel>
        </FieldWrapper>

        <FieldWrapper>
          <StyledHeader>{t('report')}</StyledHeader>
          <StyledLabel>{item.reportName}</StyledLabel>
        </FieldWrapper>

        <FieldWrapper>
          <StyledHeader>{t('template-name')}</StyledHeader>
          <StyledLabel>{item.reportTemplateName || 'N/A'}</StyledLabel>
        </FieldWrapper>

        <FieldWrapper>
          <StyledHeader>{t('format')}</StyledHeader>
          <StyledLabel>{item.extension.toUpperCase()}</StyledLabel>
        </FieldWrapper>

        <FieldWrapper>
          <StyledHeader>{t('file-size')}</StyledHeader>
          <StyledLabel>{formatFileSize(item.reportSize)}</StyledLabel>
        </FieldWrapper>

        <FieldWrapper>
          <StyledHeader>{t('request-date-time')}</StyledHeader>
          <StyledLabel>{utils.date.convertDate(item.reportRequestDate, userPreference.timeZone ?? 'UTC')}</StyledLabel>
        </FieldWrapper>

        <FieldWrapper>
          <StyledHeader>{t('availability-date-time')}</StyledHeader>
          <StyledLabel>{utils.date.convertDate(item.reportDate, userPreference.timeZone ?? 'UTC')}</StyledLabel>
        </FieldWrapper>
      </LabelsWrapper>

      <ButtonWrapper>
        <MainButton id="report-download-item-btn" value={t('download')} scope="tertiary" action={buttonAction} />
      </ButtonWrapper>
    </WrapperReportDownloadItem>
  );
};
